import { clone } from "../Handler";
export class MediaSection {
    constructor({ iceParameters, iceCandidates, dtlsParameters, planB = false }) {
        this.mediaObject = {};
        this.planB = planB;
        if (iceParameters) {
            this.setIceParameters(iceParameters);
        }
        if (iceCandidates) {
            this.mediaObject.candidates = [];
            for (const candidate of iceCandidates) {
                const candidateObject = {};
                // mediasoup does mandates rtcp-mux so candidates component is always
                // RTP (1).
                candidateObject.component = 1;
                candidateObject.foundation = candidate.foundation;
                candidateObject.ip = candidate.ip;
                candidateObject.port = candidate.port;
                candidateObject.priority = candidate.priority;
                candidateObject.transport = candidate.protocol;
                candidateObject.type = candidate.type;
                if (candidate.tcpType)
                    candidateObject.tcptype = candidate.tcpType;
                this.mediaObject.candidates.push(candidateObject);
            }
            this.mediaObject.endOfCandidates = "end-of-candidates";
            this.mediaObject.iceOptions = "renomination";
        }
        if (dtlsParameters) {
            this.setDtlsRole(dtlsParameters.role);
        }
    }
    get mid() {
        return String(this.mediaObject.mid);
    }
    get closed() {
        return this.mediaObject.port === 0;
    }
    getObject() {
        return this.mediaObject;
    }
    disable() {
        this.mediaObject.direction = "inactive";
        delete this.mediaObject.ext;
        delete this.mediaObject.ssrcs;
        delete this.mediaObject.ssrcGroups;
        delete this.mediaObject.simulcast;
        delete this.mediaObject.simulcast_03;
        delete this.mediaObject.rids;
    }
    close() {
        this.mediaObject.direction = "inactive";
        this.mediaObject.port = 0;
        delete this.mediaObject.ext;
        delete this.mediaObject.ssrcs;
        delete this.mediaObject.ssrcGroups;
        delete this.mediaObject.simulcast;
        delete this.mediaObject.simulcast_03;
        delete this.mediaObject.rids;
        delete this.mediaObject.extmapAllowMixed;
    }
    setIceParameters(iceParameters) {
        this.mediaObject.iceUfrag = iceParameters.usernameFragment;
        this.mediaObject.icePwd = iceParameters.password;
    }
}
export class AnswerMediaSection extends MediaSection {
    constructor({ iceParameters, iceCandidates, dtlsParameters, sctpParameters, plainRtpParameters, planB = false, offerMediaObject, offerRtpParameters, answerRtpParameters, codecOptions, extmapAllowMixed = false }) {
        super({ iceParameters, iceCandidates, dtlsParameters, planB });
        this.mediaObject.mid = String(offerMediaObject.mid);
        this.mediaObject.type = offerMediaObject.type;
        this.mediaObject.protocol = offerMediaObject.protocol;
        if (!plainRtpParameters) {
            this.mediaObject.connection = { ip: "127.0.0.1", version: 4 };
            this.mediaObject.port = 7;
        }
        else {
            this.mediaObject.connection = {
                ip: plainRtpParameters.ip,
                version: plainRtpParameters.ipVersion
            };
            this.mediaObject.port = plainRtpParameters.port;
        }
        switch (offerMediaObject.type) {
            case "audio":
            case "video": {
                this.mediaObject.direction = "recvonly";
                this.mediaObject.rtp = [];
                this.mediaObject.rtcpFb = [];
                this.mediaObject.fmtp = [];
                for (const codec of answerRtpParameters.codecs) {
                    const rtp = {
                        payload: codec.payloadType,
                        codec: getCodecName(codec),
                        rate: codec.clockRate
                    };
                    if (codec.channels > 1) {
                        rtp.encoding = codec.channels;
                    }
                    this.mediaObject.rtp.push(rtp);
                    const codecParameters = clone(codec.parameters, {});
                    if (codecOptions) {
                        const { opusStereo, opusFec, opusDtx, opusMaxPlaybackRate, opusMaxAverageBitrate, opusPtime, videoGoogleStartBitrate, videoGoogleMaxBitrate, videoGoogleMinBitrate } = codecOptions;
                        const offerCodec = offerRtpParameters.codecs.find((c) => c.payloadType === codec.payloadType);
                        if (offerCodec && !offerCodec.parameters) {
                            offerCodec.parameters = {};
                        }
                        switch (codec.mimeType.toLowerCase()) {
                            case "audio/opus": {
                                if (opusStereo !== undefined) {
                                    offerCodec.parameters["sprop-stereo"] = opusStereo ? 1 : 0;
                                    codecParameters.stereo = opusStereo ? 1 : 0;
                                }
                                if (opusFec !== undefined) {
                                    offerCodec.parameters.useinbandfec = opusFec ? 1 : 0;
                                    codecParameters.useinbandfec = opusFec ? 1 : 0;
                                }
                                if (opusDtx !== undefined) {
                                    offerCodec.parameters.usedtx = opusDtx ? 1 : 0;
                                    codecParameters.usedtx = opusDtx ? 1 : 0;
                                }
                                if (opusMaxPlaybackRate !== undefined) {
                                    codecParameters.maxplaybackrate = opusMaxPlaybackRate;
                                }
                                if (opusMaxAverageBitrate !== undefined) {
                                    codecParameters.maxaveragebitrate = opusMaxAverageBitrate;
                                }
                                if (opusPtime !== undefined) {
                                    offerCodec.parameters.ptime = opusPtime;
                                    codecParameters.ptime = opusPtime;
                                }
                                break;
                            }
                            case "video/vp8":
                            case "video/vp9":
                            case "video/h264":
                            case "video/h265": {
                                if (videoGoogleStartBitrate !== undefined)
                                    codecParameters["x-google-start-bitrate"] = videoGoogleStartBitrate;
                                if (videoGoogleMaxBitrate !== undefined)
                                    codecParameters["x-google-max-bitrate"] = videoGoogleMaxBitrate;
                                if (videoGoogleMinBitrate !== undefined)
                                    codecParameters["x-google-min-bitrate"] = videoGoogleMinBitrate;
                                break;
                            }
                        }
                    }
                    const fmtp = {
                        payload: codec.payloadType,
                        config: ""
                    };
                    for (const key of Object.keys(codecParameters)) {
                        if (fmtp.config)
                            fmtp.config += ";";
                        fmtp.config += `${key}=${codecParameters[key]}`;
                    }
                    if (fmtp.config) {
                        this.mediaObject.fmtp.push(fmtp);
                    }
                    for (const fb of codec.rtcpFeedback) {
                        this.mediaObject.rtcpFb.push({
                            payload: codec.payloadType,
                            type: fb.type,
                            subtype: fb.parameter
                        });
                    }
                }
                this.mediaObject.payloads = answerRtpParameters.codecs.map((codec) => codec.payloadType).join(" ");
                this.mediaObject.ext = [];
                for (const ext of answerRtpParameters.headerExtensions) {
                    // Don't add a header extension if not present in the offer.
                    const found = (offerMediaObject.ext || []).some((localExt) => localExt.uri === ext.uri);
                    if (!found)
                        continue;
                    this.mediaObject.ext.push({
                        uri: ext.uri,
                        value: ext.id
                    });
                }
                // Allow both 1 byte and 2 bytes length header extensions.
                if (extmapAllowMixed && offerMediaObject.extmapAllowMixed === "extmap-allow-mixed") {
                    this.mediaObject.extmapAllowMixed = "extmap-allow-mixed";
                }
                // Simulcast.
                if (offerMediaObject.simulcast) {
                    this.mediaObject.simulcast = {
                        dir1: "recv",
                        list1: offerMediaObject.simulcast.list1
                    };
                    this.mediaObject.rids = [];
                    for (const rid of offerMediaObject.rids || []) {
                        if (rid.direction !== "send")
                            continue;
                        this.mediaObject.rids.push({
                            id: rid.id,
                            direction: "recv"
                        });
                    }
                }
                else if (offerMediaObject.simulcast_03) {
                    // Simulcast (draft version 03).
                    // eslint-disable-next-line camelcase
                    this.mediaObject.simulcast_03 = {
                        value: offerMediaObject.simulcast_03.value.replace(/send/g, "recv")
                    };
                    this.mediaObject.rids = [];
                    for (const rid of offerMediaObject.rids || []) {
                        if (rid.direction !== "send")
                            continue;
                        this.mediaObject.rids.push({
                            id: rid.id,
                            direction: "recv"
                        });
                    }
                }
                this.mediaObject.rtcpMux = "rtcp-mux";
                this.mediaObject.rtcpRsize = "rtcp-rsize";
                if (this.planB && this.mediaObject.type === "video")
                    this.mediaObject.xGoogleFlag = "conference";
                break;
            }
            case "application": {
                // New spec.
                if (typeof offerMediaObject.sctpPort === "number") {
                    this.mediaObject.payloads = "webrtc-datachannel";
                    this.mediaObject.sctpPort = sctpParameters.port;
                    this.mediaObject.maxMessageSize = sctpParameters.maxMessageSize;
                }
                else if (offerMediaObject.sctpmap) {
                    // Old spec.
                    this.mediaObject.payloads = sctpParameters.port;
                    this.mediaObject.sctpmap = {
                        app: "webrtc-datachannel",
                        sctpmapNumber: sctpParameters.port,
                        maxMessageSize: sctpParameters.maxMessageSize
                    };
                }
                break;
            }
        }
    }
    setDtlsRole(role) {
        switch (role) {
            case "client":
                this.mediaObject.setup = "active";
                break;
            case "server":
                this.mediaObject.setup = "passive";
                break;
            case "auto":
                this.mediaObject.setup = "actpass";
                break;
        }
    }
}
export class OfferMediaSection extends MediaSection {
    constructor({ iceParameters, iceCandidates, dtlsParameters, sctpParameters, plainRtpParameters, planB = false, mid, kind, offerRtpParameters, streamId, trackId, oldDataChannelSpec = false }) {
        super({ iceParameters, iceCandidates, dtlsParameters, planB });
        this.mediaObject.mid = String(mid);
        this.mediaObject.type = kind;
        if (!plainRtpParameters) {
            this.mediaObject.connection = { ip: "127.0.0.1", version: 4 };
            if (!sctpParameters)
                this.mediaObject.protocol = "UDP/TLS/RTP/SAVPF";
            else
                this.mediaObject.protocol = "UDP/DTLS/SCTP";
            this.mediaObject.port = 7;
        }
        else {
            this.mediaObject.connection = {
                ip: plainRtpParameters.ip,
                version: plainRtpParameters.ipVersion
            };
            this.mediaObject.protocol = "RTP/AVP";
            this.mediaObject.port = plainRtpParameters.port;
        }
        switch (kind) {
            case "audio":
            case "video": {
                this.mediaObject.direction = "sendonly";
                this.mediaObject.rtp = [];
                this.mediaObject.rtcpFb = [];
                this.mediaObject.fmtp = [];
                if (!this.planB) {
                    this.mediaObject.msid = `${streamId !== null && streamId !== void 0 ? streamId : "-"} ${trackId}`;
                }
                for (const codec of offerRtpParameters.codecs) {
                    const rtp = {
                        payload: codec.payloadType,
                        codec: getCodecName(codec),
                        rate: codec.clockRate
                    };
                    if (codec.channels > 1) {
                        rtp.encoding = codec.channels;
                    }
                    this.mediaObject.rtp.push(rtp);
                    const fmtp = {
                        payload: codec.payloadType,
                        config: ""
                    };
                    for (const key of Object.keys(codec.parameters)) {
                        if (fmtp.config) {
                            fmtp.config += ";";
                        }
                        fmtp.config += `${key}=${codec.parameters[key]}`;
                    }
                    if (fmtp.config) {
                        this.mediaObject.fmtp.push(fmtp);
                    }
                    for (const fb of codec.rtcpFeedback) {
                        this.mediaObject.rtcpFb.push({
                            payload: codec.payloadType,
                            type: fb.type,
                            subtype: fb.parameter
                        });
                    }
                }
                this.mediaObject.payloads = offerRtpParameters.codecs.map((codec) => codec.payloadType).join(" ");
                this.mediaObject.ext = [];
                if (offerRtpParameters && offerRtpParameters.headerExtensions) {
                    for (const ext of offerRtpParameters.headerExtensions) {
                        this.mediaObject.ext.push({
                            uri: ext.uri,
                            value: ext.id
                        });
                    }
                }
                this.mediaObject.rtcpMux = "rtcp-mux";
                this.mediaObject.rtcpRsize = "rtcp-rsize";
                this.mediaObject.ssrcs = [];
                this.mediaObject.ssrcGroups = [];
                if (offerRtpParameters && offerRtpParameters.encodings && offerRtpParameters.encodings.length > 0) {
                    const encoding = offerRtpParameters.encodings[0];
                    const ssrc = encoding.ssrc;
                    const rtxSsrc = encoding.rtx && encoding.rtx.ssrc ? encoding.rtx.ssrc : undefined;
                    if (offerRtpParameters.rtcp.cname) {
                        this.mediaObject.ssrcs.push({
                            id: ssrc,
                            attribute: "cname",
                            value: offerRtpParameters.rtcp.cname
                        });
                    }
                    if (this.planB) {
                        this.mediaObject.ssrcs.push({
                            id: ssrc,
                            attribute: "msid",
                            value: `${streamId !== null && streamId !== void 0 ? streamId : "-"} ${trackId}`
                        });
                    }
                    if (rtxSsrc) {
                        if (offerRtpParameters.rtcp.cname) {
                            this.mediaObject.ssrcs.push({
                                id: rtxSsrc,
                                attribute: "cname",
                                value: offerRtpParameters.rtcp.cname
                            });
                        }
                        if (this.planB) {
                            this.mediaObject.ssrcs.push({
                                id: rtxSsrc,
                                attribute: "msid",
                                value: `${streamId !== null && streamId !== void 0 ? streamId : "-"} ${trackId}`
                            });
                        }
                        // Associate original and retransmission SSRCs.
                        this.mediaObject.ssrcGroups.push({
                            semantics: "FID",
                            ssrcs: `${ssrc} ${rtxSsrc}`
                        });
                    }
                }
                break;
            }
            case "application": {
                if (!oldDataChannelSpec) {
                    this.mediaObject.payloads = "webrtc-datachannel";
                    this.mediaObject.sctpPort = sctpParameters.port;
                    this.mediaObject.maxMessageSize = sctpParameters.maxMessageSize;
                }
                else {
                    // Old spec.
                    this.mediaObject.payloads = sctpParameters.port;
                    this.mediaObject.sctpmap = {
                        app: "webrtc-datachannel",
                        sctpmapNumber: sctpParameters.port,
                        maxMessageSize: sctpParameters.maxMessageSize
                    };
                }
                break;
            }
        }
    }
    setDtlsRole(_role) { }
}
function getCodecName(codec) {
    const MimeTypeRegex = /^(audio|video)\/(.+)/i;
    const mimeTypeMatch = MimeTypeRegex.exec(codec.mimeType);
    if (!mimeTypeMatch)
        throw new TypeError("invalid codec.mimeType");
    return mimeTypeMatch[2];
}
